import Workflow, { WorkflowAction } from 'models/Workflow';

// The completed steps a correction would ripple into (design §9) — what the restart-consent step
// lists before anything saves. Shared by Edit Demographics and the Patient Demographics card's
// "Use this value" (OLIO-3252), so the two can't disagree about what a change re-runs.
//
// `names` are referral-level value names that changed; `coverages` the coverages corrected, each with
// the coverage-level names that changed on it. A coverage correction re-runs that coverage's own steps
// — scoped, so fixing one coverage doesn't ask to re-run the other's paid check — and only the steps
// the changed field feeds: a member ID fix re-runs the verification, but not the payer search, which
// would throw away the payer already picked and ask for it again.
// Empty on a terminal run: the API rejects restarts there.
//
// Never a step that can't be re-run (`rerunnable === false`): the check on a coverage whose benefits
// were documented by hand (OLIO-3229). That coverage stays manual, so neither a demographic nor a
// coverage edit restarts it, and the API skips it if asked; listing it would ask consent for a restart
// that never happens. Its payer search is Not Applicable (`skipped`), which `impactedActionsBy` already
// leaves out.
export type CoverageCorrection = { facetId: string; names: string[] };

export function impactedActionsFor(
  workflow: Workflow,
  { names = [], coverages = [] }: { names?: string[]; coverages?: CoverageCorrection[] }
): WorkflowAction[] {
  if (workflow.isTerminal) return [];

  const fromValues = names.length > 0 ? workflow.impactedActionsBy(names) : [];
  const fromCoverages = coverages.flatMap(({ facetId, names: changed }) =>
    changed.length > 0 ? workflow.impactedActionsBy(changed, [facetId]) : []
  );

  return [...fromValues, ...fromCoverages].filter(
    (action, index, all) => action.rerunnable !== false && all.findIndex((other) => other.id === action.id) === index
  );
}

// The restart list a person consented to, less any step the coverage writes already redid. Both
// correction flows write coverages first, as their own requests, and the API settles each one inline:
// a completed payer search or verification whose payer or member ID changed is redone right there, so
// by the time the referral write carries the consent, that instance is no longer current. The API
// refuses the whole write over one stale id, which dropped every other change in the same save.
//
// Filtered against the workflow the last coverage write returned rather than dropping the coverage
// steps outright: the engine only redoes COMPLETED steps, so one that was waiting on a pick, running or
// errored is still current and still needs the restart the person asked for.
//
// Except a step whose inputs come from a producer the coverage write just put back in flight. A payer
// name edit redoes Payer Search make-before-break, so the previous pick's `external_payer_id` stays
// current until a new payer is picked, and the verification isn't invalidated by the payer name — it
// survives the write. Restarting it now would dispatch at once against the payer the person just
// corrected away: a billed check against the wrong payer, then a second one after the pick. Left alone,
// a completed verification is superseded by the new pick and redone by the engine against the right
// payer. One that wasn't completed (errored, say) is not redone that way; it stays as it is until the
// person runs it again from the slideout once the new payer is picked.
//
// Undefined when nothing is left, so a save whose only change was the coverage makes no referral write.
export function stillCurrentRestartIds(settled: Workflow, ids?: string[]): string[] | undefined {
  if (!ids) return undefined;

  const kept = ids.filter((id) => {
    const action = settled.actions.find((candidate) => candidate.id === id);
    return action && settled.inFlightProducersFor(action, { awaitingInput: true }).length === 0;
  });
  return kept.length > 0 ? kept : undefined;
}
